import { http } from "msw";
import type { Page, Post } from "../../api/types";
import { paginateCursor, sortBy } from "../engine";
import * as viewerState from "../viewer-state";
import { VIEWER_USERNAME } from "../viewer-state";
import { allPosts, decoratePost, mockJson, parsePositiveInt } from "./shared";

type FeedTab = "for-you" | "following" | "trending";

function isViewerOrFollowed(post: Post): boolean {
  return post.author.username === VIEWER_USERNAME || viewerState.isFollowingCreator(post.author.username);
}

async function feedPool(tab: FeedTab): Promise<Post[]> {
  const posts = await allPosts();
  if (tab === "following") return posts.filter(isViewerOrFollowed);
  if (tab === "trending") return sortBy(posts, (p) => -(p.likes + p.comments * 2 + p.reposts * 3));
  return sortBy(posts, (p) => -Date.parse(p.createdAt));
}

export const feedHandlers = [
  http.get("/api/feed", async ({ request }) => {
    const url = new URL(request.url);
    const tab = (url.searchParams.get("tab") as FeedTab | null) ?? "for-you";
    const cursor = url.searchParams.get("cursor");
    const limit = parsePositiveInt(url.searchParams.get("limit"), 20);

    const pool = await feedPool(tab);
    const page = paginateCursor(pool, cursor, limit);
    const body: Page<Post> = { items: page.items.map(decoratePost), nextCursor: page.nextCursor };
    return mockJson(body);
  }),

  http.get("/api/trending", async () => {
    const posts = await allPosts();
    const tagCounts = new Map<string, number>();
    for (const post of posts) {
      for (const tag of post.tags) tagCounts.set(tag, (tagCounts.get(tag) ?? 0) + 1);
    }
    const tags = [...tagCounts.entries()]
      .sort((a, b) => b[1] - a[1])
      .slice(0, 10)
      .map(([tag, count]) => ({ tag, posts: count }));
    return mockJson({ tags });
  }),
];
